import './index.scss';

import { ErrorMessage, Field, Form as FormikForm, Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { FaPlus } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import axios from '../../admin-axios';
import useActions from '../../hooks/useActions';
import { detectError } from '../../utils';
import LongPaginationHandler from '../LongPaginationHandler';
import { SuccessModal } from '../Modals';
import CINTableRow from './CINTableRow';

const initialValues = {
  firstname: '',
  lastname: '',
  phone: '',
  email: '',
  customer_type: ''
};

const phoneRules =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const validationSchema = Yup.object({
  firstname: Yup.string().required('Required'),
  lastname: Yup.string().required('Required'),
  phone: Yup.string()
    .min(11, 'phone number should be 11 chars')
    .max(11, 'Phone number should be 11 chars')
    .matches(phoneRules, { message: 'please enter a valid phone number' })
    .required(' Phone number is required'),
  email: Yup.string().email('email is not valid').required('Required'),
  customer_type: Yup.string().required('Required')
});

const CustomersCINTable = ({ match }) => {
  const [showForm, setShowForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const searchRef = useRef();

  const {
    user: { stationId, id }
  } = useSelector((state) => state.auth);

  const {
    customers,
    customers_loading,
    customers_total,
    customers_per_page,
    customers_page,
    searchResults,
    searchResultsLoading,
    customerView
  } = useSelector((state) => state.customers);
  const { getCustomers, searchCustomers, setCustomerView } = useActions();
  // const { setBackLink } = useActions();

  const paginate = (pageNumber) => {
    getCustomers(stationId, pageNumber);
  };

  const searchHandler = (event) => {
    if (event.code === 'Enter') {
      setCustomerView('searching');
      searchCustomers(stationId, searchRef.current.value);
      searchRef.current.value = '';
    }
  };

  useEffect(() => {
    if (!customers) {
      getCustomers(stationId, customers_page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers_page, stationId, customers]);

  const handleFormClose = () => setShowForm(false);
  const handleFormShow = () => setShowForm(true);

  return (
    <>
      <div className="customers_cin">
        <div className="customers_cin_search mb-3">
          <input
            type="text"
            className="form-control p-2 customers_cin_search_input"
            placeholder="Search by Phone number"
            aria-label="search"
            ref={searchRef}
            onKeyDown={searchHandler}
          />
        </div>
        <div className="d-flex justify-content-between mb-4">
          <h3>All Customers</h3>
          <button
            onClick={handleFormShow}
            type="button"
            className="btn p-3 customers_cin_register_button">
            <FaPlus /> Register Customer
          </button>
        </div>
        {customerView === 'searching' && (
          <div className="">
            <button
              onClick={() => setCustomerView('retailers')}
              type="button"
              className="mb-3 btn customers_cin_register_button">
              Back to All Customers
            </button>
          </div>
        )}
        <div className="customers_cin_table_wrapper">
          <table className="table table-striped customers_cin_table">
            <thead>
              <tr>
                <th scope="col">Customer Name</th>
                <th scope="col">Phone No.</th>
                <th scope="col">Email Address</th>
                <th scope="col">Customer Type</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            {customerView === 'searching' && (
              <CINTableRow
                customers={searchResults}
                loading={searchResultsLoading}
                path={match.url}
              />
            )}
            {(customerView === 'retailers' || customerView === 'endusers') && (
              <CINTableRow customers={customers} loading={customers_loading} path={match.url} />
            )}
          </table>
        </div>
        {(customerView === 'retailers' || customerView === 'endusers') && (
          <LongPaginationHandler
            elementsPerPage={customers_per_page || 20}
            totalElements={customers_total}
            activePage={customers_page}
            paginate={paginate}
          />
        )}
      </div>
      <div className="customers_cin_modals">
        <Modal show={showForm} onHide={handleFormClose}>
          <Modal.Header closeButton>
            <Modal.Title className="customers_cin_modals_form_title">
              <span>Register Customer</span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={async (values) => {
                try {
                  setFormLoading(true);

                  // Register new staff member
                  await axios.post(`/customer/add`, {
                    firstname: values.firstname,
                    lastname: values.lastname,
                    phone: values.phone,
                    email: values.email,
                    customer_type: values.customer_type,
                    created_by: id,
                    station_id: stationId
                  });

                  setFormLoading(false);
                  setShowSuccess(true);
                  handleFormClose();
                  getCustomers(stationId, customers_page);
                } catch (error) {
                  setFormLoading(false);
                  toast.error(detectError(error));
                }
              }}>
              {(formik) => {
                return (
                  <FormikForm className="mb-5">
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="firstname" className="fw-bold">
                        First Name
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        placeholder="First Name"
                        name="firstname"
                        id="firstname"
                      />
                      <ErrorMessage component="div" className="text-danger p-0" name="firstname" />
                    </div>
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="lastname" className="fw-bold">
                        Last Name
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        placeholder="Last Name"
                        name="lastname"
                        id="lastname"
                      />
                      <ErrorMessage component="div" className="text-danger p-0" name="lastname" />
                    </div>
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="phonenumber" className="fw-bold">
                        Phone Number
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        placeholder="Phone Number"
                        name="phone"
                        id="phonenumber"
                      />
                      <ErrorMessage component="div" className="text-danger p-0" name="phone" />
                    </div>
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor="email" className="fw-bold">
                        Email
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        placeholder="Email"
                        name="email"
                        id="email"
                      />
                      <ErrorMessage component="div" className="text-danger p-0" name="email" />
                    </div>
                    <div className="mb-2">
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label className="fw-bold">Customer Type</label>
                      <Field as="select" className="form-select" name="customer_type">
                        <option value="">Options</option>
                        <option value="retailer">Retailer</option>
                        <option value="end user">End User</option>
                      </Field>
                      <ErrorMessage
                        component="div"
                        className="text-danger p-0"
                        name="customer_type"
                      />
                    </div>
                    <div className="mb-2">
                      {/* {error && <p>{error}</p>} */}
                      {formLoading ? (
                        <div className="text-center d-flex justify-content-center w-100 h-100">
                          <Spinner animation="border" variant="dark" />
                        </div>
                      ) : (
                        <Button
                          disabled={!formik.isValid}
                          className="w-100 customers_cin_modals_form_button"
                          variant="primary"
                          type="submit">
                          Register Customer
                        </Button>
                      )}
                    </div>
                  </FormikForm>
                );
              }}
            </Formik>
          </Modal.Body>
        </Modal>
        <SuccessModal
          message={"You've successfully registered a new customer"}
          showSuccess={showSuccess}
          setShowSuccess={setShowSuccess}
        />
      </div>
    </>
  );
};

export default CustomersCINTable;
